import React, { ElementType } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { AddIcon, ChevronDownIcon, SearchIcon } from '@chakra-ui/icons';
import { FiGrid, FiList } from 'react-icons/fi';

export const catalogueAccent = '#12805c';
export const catalogueAccentDark = '#0f6a4d';
export const catalogueSurface = 'white';
export const catalogueSurfaceMuted = 'gray.50';
export const catalogueBorder = 'gray.200';

export const formatCurrency = (value?: number | string | null) => {
  const numericValue =
    typeof value === 'string' ? Number.parseFloat(value) : value ?? 0;

  if (Number.isNaN(Number(numericValue))) {
    return '$0.00';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(numericValue));
};

export const formatDateLabel = (value?: string | null) => {
  if (!value) {
    return 'Recently updated';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Recently updated';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const CatalogueShell = ({
  children,
  compact = false,
}: {
  children: React.ReactNode;
  compact?: boolean;
}) => (
  <Box
    px={{ base: 4, md: 6, xl: 8 }}
    py={{ base: 4, md: 6 }}
    bg="linear-gradient(180deg, #f7faf8 0%, #ffffff 32%)"
    minH="calc(100vh - 72px)"
  >
    <Box maxW={compact ? '1120px' : '1280px'} mx="auto">
      {children}
    </Box>
  </Box>
);

export const CatalogueHero = ({
  eyebrow,
  title,
  description,
  primaryAction,
  secondaryAction,
  stats,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  stats?: Array<{ label: string; value: string }>;
}) => (
  <Box
    bg={catalogueSurface}
    borderWidth="1px"
    borderColor={catalogueBorder}
    borderRadius={{ base: '24px', md: '28px' }}
    px={{ base: 5, md: 7 }}
    py={{ base: 5, md: 7 }}
    boxShadow="0 18px 48px rgba(15, 23, 42, 0.06)"
    mb={6}
  >
    <Flex
      align={{ base: 'stretch', lg: 'flex-start' }}
      justify="space-between"
      gap={6}
      direction={{ base: 'column', lg: 'row' }}
    >
      <VStack align="flex-start" spacing={3} maxW="3xl">
        {eyebrow ? (
          <Text
            fontSize="xs"
            textTransform="uppercase"
            letterSpacing="0.18em"
            fontWeight="700"
            color={catalogueAccent}
          >
            {eyebrow}
          </Text>
        ) : null}
        <Text
          as="h1"
          fontSize={{ base: '2xl', md: '4xl' }}
          fontWeight="800"
          lineHeight={{ base: '1.2', md: '1.05' }}
          color="gray.900"
          letterSpacing="-0.03em"
        >
          {title}
        </Text>
        <Text fontSize={{ base: 'sm', md: 'md' }} color="gray.600" maxW="2xl">
          {description}
        </Text>
        {stats && stats.length > 0 ? (
          <SimpleGrid
            columns={{ base: 2, md: Math.min(stats.length, 4) }}
            spacing={3}
            w="full"
            pt={2}
          >
            {stats.map((stat) => (
              <Box
                key={stat.label}
                borderWidth="1px"
                borderColor="gray.100"
                bg={catalogueSurfaceMuted}
                borderRadius="18px"
                px={4}
                py={3}
              >
                <Text fontSize="xs" color="gray.500" mb={1}>
                  {stat.label}
                </Text>
                <Text fontSize="lg" fontWeight="700" color="gray.900">
                  {stat.value}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        ) : null}
      </VStack>
      {(primaryAction || secondaryAction) && (
        <Stack
          direction={{ base: 'column', sm: 'row', lg: 'column' }}
          spacing={3}
          minW={{ lg: '220px' }}
        >
          {primaryAction}
          {secondaryAction}
        </Stack>
      )}
    </Flex>
  </Box>
);

export const PrimaryCatalogueButton = ({
  children,
  ...props
}: React.ComponentProps<typeof Button>) => (
  <Button
    bg={catalogueAccent}
    color="white"
    _hover={{ bg: catalogueAccentDark, transform: 'translateY(-1px)' }}
    _active={{ bg: catalogueAccentDark }}
    boxShadow="0 12px 26px rgba(18, 128, 92, 0.22)"
    borderRadius="16px"
    fontWeight="700"
    px={5}
    {...props}
  >
    {children}
  </Button>
);

export const CatalogueSectionCard = ({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box
    bg={catalogueSurface}
    borderWidth="1px"
    borderColor={catalogueBorder}
    borderRadius="24px"
    px={{ base: 4, md: 6 }}
    py={{ base: 4, md: 5 }}
    boxShadow="0 12px 32px rgba(15, 23, 42, 0.04)"
  >
    <Flex
      justify="space-between"
      align={{ base: 'flex-start', md: 'center' }}
      direction={{ base: 'column', md: 'row' }}
      gap={3}
      mb={5}
    >
      <Box>
        <Text
          fontSize={{ base: 'lg', md: 'xl' }}
          fontWeight="800"
          letterSpacing="-0.02em"
          color="gray.900"
        >
          {title}
        </Text>
        {description ? (
          <Text fontSize="sm" color="gray.500" mt={1}>
            {description}
          </Text>
        ) : null}
      </Box>
      {action}
    </Flex>
    {children}
  </Box>
);

export const CatalogueCommandBar = ({
  query,
  onQueryChange,
  sort,
  onSortChange,
  status,
  onStatusChange,
  view,
  onViewChange,
  resultLabel,
  addMenu,
}: {
  query: string;
  onQueryChange: (value: string) => void;
  sort: string;
  onSortChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  view: 'table' | 'grid';
  onViewChange: (value: 'table' | 'grid') => void;
  resultLabel: string;
  addMenu?: React.ReactNode;
}) => (
  <Box
    bg="rgba(255, 255, 255, 0.9)"
    borderWidth="1px"
    borderColor={catalogueBorder}
    backdropFilter="blur(12px)"
    borderRadius="24px"
    px={{ base: 4, md: 5 }}
    py={{ base: 4, md: 4 }}
    position="sticky"
    top={{ base: 70, md: 78 }}
    zIndex={5}
    boxShadow="0 14px 36px rgba(15, 23, 42, 0.05)"
    mb={6}
  >
    <Stack spacing={4}>
      <Flex
        gap={3}
        wrap="wrap"
        align={{ base: 'stretch', md: 'center' }}
        justify="space-between"
      >
        <InputGroup maxW={{ base: 'full', lg: '460px' }}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Search by name, price, or description"
            bg={catalogueSurfaceMuted}
            borderColor="gray.100"
            borderRadius="16px"
            _focusVisible={{
              borderColor: catalogueAccent,
              boxShadow: `0 0 0 1px ${catalogueAccent}`,
            }}
          />
        </InputGroup>
        <HStack spacing={2} flexWrap="wrap">
          <Select
            value={status}
            onChange={(event) => onStatusChange(event.target.value)}
            maxW={{ base: 'full', sm: '180px' }}
            bg={catalogueSurfaceMuted}
            borderRadius="16px"
            borderColor="gray.100"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="with-images">With images</option>
            <option value="without-images">Without images</option>
          </Select>
          <Select
            value={sort}
            onChange={(event) => onSortChange(event.target.value)}
            maxW={{ base: 'full', sm: '180px' }}
            bg={catalogueSurfaceMuted}
            borderRadius="16px"
            borderColor="gray.100"
          >
            <option value="updated">Recently updated</option>
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
            <option value="price-high">Price high-low</option>
            <option value="price-low">Price low-high</option>
          </Select>
          <HStack
            spacing={1}
            p={1}
            borderWidth="1px"
            borderColor="gray.100"
            borderRadius="16px"
            bg={catalogueSurfaceMuted}
          >
            <IconButton
              aria-label="Table view"
              icon={<Box as={FiList as ElementType} />}
              size="sm"
              variant={view === 'table' ? 'solid' : 'ghost'}
              bg={view === 'table' ? catalogueAccent : 'transparent'}
              color={view === 'table' ? 'white' : 'gray.600'}
              _hover={{
                bg: view === 'table' ? catalogueAccentDark : 'gray.100',
              }}
              onClick={() => onViewChange('table')}
            />
            <IconButton
              aria-label="Grid view"
              icon={<Box as={FiGrid as ElementType} />}
              size="sm"
              variant={view === 'grid' ? 'solid' : 'ghost'}
              bg={view === 'grid' ? catalogueAccent : 'transparent'}
              color={view === 'grid' ? 'white' : 'gray.600'}
              _hover={{
                bg: view === 'grid' ? catalogueAccentDark : 'gray.100',
              }}
              onClick={() => onViewChange('grid')}
            />
          </HStack>
          {addMenu}
        </HStack>
      </Flex>
      <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
        <Text fontSize="sm" color="gray.500">
          {resultLabel}
        </Text>
      </Flex>
    </Stack>
  </Box>
);

export const CatalogueStatusBadge = ({
  isActive,
  imageCount,
}: {
  isActive: boolean;
  imageCount?: number;
}) => {
  if (!isActive) {
    return (
      <Badge colorScheme="gray" borderRadius="full" px={3} py={1}>
        Inactive
      </Badge>
    );
  }

  if (!imageCount) {
    return (
      <Badge colorScheme="orange" borderRadius="full" px={3} py={1}>
        Needs image
      </Badge>
    );
  }

  return (
    <Badge colorScheme="green" borderRadius="full" px={3} py={1}>
      Active
    </Badge>
  );
};

export const CatalogueEmptyState = ({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
  icon?: ElementType;
}) => (
  <VStack
    bg={catalogueSurface}
    borderWidth="1px"
    borderColor={catalogueBorder}
    borderRadius="28px"
    px={{ base: 6, md: 10 }}
    py={{ base: 10, md: 14 }}
    spacing={4}
    textAlign="center"
    boxShadow="0 12px 32px rgba(15, 23, 42, 0.04)"
  >
    <Box
      w="64px"
      h="64px"
      borderRadius="20px"
      bg="rgba(18, 128, 92, 0.10)"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {icon ? <Box as={icon} boxSize={7} color={catalogueAccent} /> : null}
    </Box>
    <VStack spacing={2}>
      <Text fontSize="xl" fontWeight="800" color="gray.900">
        {title}
      </Text>
      <Text fontSize="sm" color="gray.500" maxW="md">
        {description}
      </Text>
    </VStack>
    {action}
  </VStack>
);

export const CatalogueListSkeleton = () => (
  <Stack spacing={4}>
    {[...Array(5)].map((_, index) => (
      <Box
        key={index}
        bg={catalogueSurface}
        borderWidth="1px"
        borderColor={catalogueBorder}
        borderRadius="22px"
        px={5}
        py={4}
      >
        <HStack spacing={4} align="flex-start">
          <Skeleton h="72px" w="72px" borderRadius="18px" />
          <Box flex="1">
            <Skeleton h="18px" w="30%" mb={3} />
            <SkeletonText noOfLines={2} spacing="3" />
          </Box>
          <Skeleton h="36px" w="90px" borderRadius="full" />
        </HStack>
      </Box>
    ))}
  </Stack>
);

export const CatalogueField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Box>
    <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="gray.500" mb={1}>
      {label}
    </Text>
    <Text color="gray.900" fontWeight="600">
      {value}
    </Text>
  </Box>
);

export const CatalogueCreateMenu = ({
  onCreateManual,
  onCreateFromText,
  onCreateFromImage,
  onCreateFromVoice,
  isDisabled = false,
}: {
  onCreateManual: () => void;
  onCreateFromText: () => void;
  onCreateFromImage: () => void;
  onCreateFromVoice: () => void;
  isDisabled?: boolean;
}) => (
  <Menu>
    <MenuButton
      as={Button}
      rightIcon={<ChevronDownIcon />}
      leftIcon={<AddIcon />}
      borderRadius="16px"
      bg={catalogueAccent}
      color="white"
      _hover={{ bg: catalogueAccentDark }}
      _active={{ bg: catalogueAccentDark }}
      isDisabled={isDisabled}
    >
      Add item
    </MenuButton>
    <MenuList borderRadius="18px" borderColor="gray.100" py={2}>
      <MenuItem onClick={onCreateManual}>Create manually</MenuItem>
      <MenuItem onClick={onCreateFromText}>Generate from text</MenuItem>
      <MenuItem onClick={onCreateFromImage}>Extract from image</MenuItem>
      <MenuItem onClick={onCreateFromVoice}>Generate from voice note</MenuItem>
    </MenuList>
  </Menu>
);
